import { useQuery } from '@apollo/client'
import { useIsMobile } from '@chain-zen/ui'
import { useState } from 'react'

import { CARRIER_HUB_POSTS } from '../../data/queries/posts'
import type { ICarrierHubPostQueryInput, ICarrierHubPostsReturnType } from '../../types/carrier-hub'
import type { IPublicPostData } from '../../types/post'
import type { IShipment } from '../../types/shipment'
import { formatDateRangeForFilters } from '../../utils/datetime-utils'
import { generateLoadboardURL } from './utils'

interface IUseBackhaulPostsProps {
  itemsPerPage?: number
  shipment: IShipment
}

interface IUseBackhaulPostsReturn {
  isLoading: boolean
  loadsPageURL: string
  posts: IPublicPostData[]
  workspacename: string
  enterpriseWorkspaceID: string | null | undefined
}

/**
 * Maps shipment data to carrier hub post query input
 */
const mapShipmentToQueryInput = (
  shipment: IShipment,
  itemsPerPage: number,
  shouldUseInitialFilter: boolean
): ICarrierHubPostQueryInput | null => {
  const enterpriseWorkspaceID = shipment?.shipmentOwnerWorkspace?.enterpriseWorkspaceID
  const workspaceID = shipment?.shipmentOwnerWorkspace?.id

  // Use enterpriseWorkspaceID if available, otherwise use workspaceID
  const finalWorkspaceID = enterpriseWorkspaceID || workspaceID
  if (!finalWorkspaceID) return null

  const stops = shipment.stops || []
  if (stops.length === 0) {
    return {
      page: 1,
      workspaceID: finalWorkspaceID,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      ...(itemsPerPage && { limit: itemsPerPage }),
      isEnterpriseWorkspace: !!enterpriseWorkspaceID
    }
  }

  // Get first and last stops
  const [firstStop, ...remainingStops] = stops
  const lastStop = remainingStops[remainingStops.length - 1] || firstStop

  // For backhauls: origin is where shipment ends (lastStop), destination is where shipment starts (firstStop)
  const originCoordinates = lastStop?.warehouse?.address?.coordinates
  const destinationCoordinates = firstStop?.warehouse?.address?.coordinates
  const originAppointmentDate = lastStop?.appointment?.start?.date

  const input: ICarrierHubPostQueryInput = {
    page: 1,
    workspaceID: finalWorkspaceID,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    ...(itemsPerPage && { limit: itemsPerPage }),
    isEnterpriseWorkspace: !!enterpriseWorkspaceID
  }

  // Handle appointment date range - use origin appointment date (from lastStop)
  if (originAppointmentDate) {
    const currentDate = new Date(originAppointmentDate)
    // Validate that the date is valid before using it
    if (!isNaN(currentDate.getTime())) {
      const nextDate = new Date(currentDate)
      nextDate.setDate(nextDate.getDate() + 1)
      input.originAppointmentDateRange = {
        endDate: formatDateRangeForFilters([currentDate, nextDate])?.endDate || '',
        startDate: formatDateRangeForFilters([currentDate, nextDate])?.startDate || ''
      }
    }
  }

  // Handle location filters - origin is lastStop, destination is firstStop
  const origin = originCoordinates
  const destination = destinationCoordinates

  if (origin && destination && shouldUseInitialFilter) {
    // Both origin and destination required for initial filter
    input.originLocation = {
      latitude: origin.latitude,
      longitude: origin.longitude,
      radiusMiles: 150
    }
    input.destinationLocation = {
      latitude: destination.latitude,
      longitude: destination.longitude,
      radiusMiles: 150
    }
  } else if (origin) {
    // Fallback filter - either origin or destination
    input.originLocation = {
      latitude: origin.latitude,
      longitude: origin.longitude,
      radiusMiles: 150
    }
  } else if (destination) {
    // Fallback filter - either origin or destination
    input.destinationLocation = {
      latitude: destination.latitude,
      longitude: destination.longitude,
      radiusMiles: 150
    }
  }

  return input
}

export function useBackhaulPosts({ itemsPerPage, shipment }: IUseBackhaulPostsProps): IUseBackhaulPostsReturn {
  const isMobile = useIsMobile()

  // State to track whether to use the default filter or fallback filter
  const [shouldUseInitialFilter, setShouldUseInitialFilter] = useState(true)

  const workspacename = shipment?.shipmentOwnerWorkspace?.workspacename || ''
  const workspaceID = shipment?.shipmentOwnerWorkspace?.id || ''

  // Generate query input based on current filter type
  const queryInput = mapShipmentToQueryInput(shipment, itemsPerPage || 4, shouldUseInitialFilter)

  // Skip query on mobile or if no valid input
  const skip = isMobile || !queryInput

  const { data, loading } = useQuery<ICarrierHubPostsReturnType, { input: ICarrierHubPostQueryInput }>(
    CARRIER_HUB_POSTS,
    {
      context: {
        headers: {
          'Ws-id': workspaceID
        }
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onCompleted: (data) => {
        if (shouldUseInitialFilter && data?.carrierHubPosts?.posts?.length === 0) {
          setShouldUseInitialFilter(false)
        }
      },
      skip,
      variables: queryInput
        ? {
            input: queryInput
          }
        : undefined
    }
  )

  const posts = data?.carrierHubPosts?.posts || []

  const loadsPageURL = generateLoadboardURL({
    filterType: shouldUseInitialFilter ? 'initialFilter' : 'fallbackFilter',
    hasAvailableBackhauls: posts.length > 0,
    shipment,
    workspacename
  })

  const enterpriseWorkspaceID = shipment?.shipmentOwnerWorkspace?.enterpriseWorkspaceID

  return {
    isLoading: loading,
    loadsPageURL,
    posts,
    workspacename,
    enterpriseWorkspaceID
  }
}
